import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import FlightTabs from "../AllTabs/FlightTabs";

const FlightReport = () => {
  const { user, loading } = useAuth();
  
  // --- Date Helpers ---
  const getTodayDate = () => {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const localDate = new Date(today.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split("T")[0];
  };

  const getFirstDayOfMonth = () => {
    const date = new Date();
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const offset = firstDay.getTimezoneOffset();
    const localDate = new Date(firstDay.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split("T")[0];
  };

  // --- State Management ---
  const [pageLoading, setPageLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [totalProfit, setTotalProfit] = useState(0);

  // Filter States
  const [fromDate, setFromDate] = useState(getFirstDayOfMonth());
  const [toDate, setToDate] = useState(getTodayDate());
  const [bookingStatus, setBookingStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [bookingNo, setBookingNo] = useState("");

  // --- API Call ---
  const GetData = async (overrideParams = null) => {
    if (!user?.cid) return;

    setPageLoading(true);

    // Construct the payload matching FlightBookingReportRequest DTO
    const requestPayload = overrideParams || {
      CompanyId: user.cid,
      CustomerName: customerName || null,
      BookingNo: bookingNo || null,
      BookingStatus: bookingStatus || null,
      PaymentStatus: paymentStatus || null,
      FromDate: fromDate || null,
      ToDate: toDate || null
    };
    console.log("Request Payload:", requestPayload);
    try {
      // NOTE: Ensure config.GETBYCOMPANYREPORT matches your Controller Route
      const res = await apiService.post(
        config.GETFLGIHTREPORTBYCOMPANYID, 
        requestPayload
      );

      if (res) {
        setLists(res);
        // Calculate Total Profit (assuming convenienceFee is the profit based on old code)
        const total = res.reduce(
          (sum, booking) => sum + (booking.convenienceFee || 0),
          0
        );
        setTotalProfit(total);
      }
    } catch (error) {
      console.error("Failed to fetch flight report data:", error);
      setLists([]);
      setTotalProfit(0);
    } finally {
      setPageLoading(false);
    }
  };

  // --- Handlers ---
  const handleReset = () => {
    const defaultFrom = getFirstDayOfMonth();
    const defaultTo = getTodayDate();
    
    // Reset UI states
    setFromDate(defaultFrom);
    setToDate(defaultTo);
    setBookingStatus("");
    setPaymentStatus("");
    setCustomerName("");
    setBookingNo("");

    // Trigger search with default values
    GetData({
      CompanyId: user.cid,
      CustomerName: null,
      BookingNo: null,
      BookingStatus: null,
      PaymentStatus: null,
      FromDate: defaultFrom,
      ToDate: defaultTo
    });
  };

  // Initial Load
  useEffect(() => {
    if (user && !loading) {
      GetData();
    }
  }, [user, loading]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) return <p>Loading user data...</p>;

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <FlightTabs pageName="FLIGHTREPORT" />
                </div>
                
                <div className="card-body">
                  
                  {/* --- Filter Section --- */}
                  <div className="row mb-4 g-3">
                    {/* Customer Name */}
                    <div className="col-md-2">
                        <label className="form-label">Customer Name</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            placeholder="Name..." 
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                        />
                    </div>

                    {/* Booking No */}
                    <div className="col-md-2">
                        <label className="form-label">Booking No</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            placeholder="Ref No..." 
                            value={bookingNo}
                            onChange={(e) => setBookingNo(e.target.value)}
                        />
                    </div>

                    {/* Booking Status */}
                    <div className="col-md-2">
                        <label className="form-label">Booking Status</label>
                        <select
                            className="form-select"
                            value={bookingStatus}
                            onChange={(e) => setBookingStatus(e.target.value)}
                        >
                            <option value="">All</option>
                            <option value="CONFIRMED">CONFIRMED</option>
                            <option value="PENDING">PENDING</option>
                            <option value="CANCELLED">CANCELLED</option>
                            <option value="FAILED">FAILED</option>
                        </select>
                    </div>

                    {/* Payment Status */}
                    <div className="col-md-2">
                        <label className="form-label">Payment Status</label>
                        <select
                            className="form-select"
                            value={paymentStatus}
                            onChange={(e) => setPaymentStatus(e.target.value)}
                        >
                            <option value="">All</option>
                            <option value="SUCCESS">SUCCESS</option>
                            <option value="PENDING">PENDING</option>
                            <option value="FAILED">FAILED</option>
                        </select>
                    </div>

                    {/* From Date */}
                    <div className="col-md-2">
                        <label className="form-label">From Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </div>

                    {/* To Date */}
                    <div className="col-md-2">
                        <label className="form-label">To Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </div>

                    {/* Buttons Row */}
                    <div className="col-12 text-end">
                         <button
                            className="btn btn-primary me-2"
                            onClick={() => GetData(null)}
                        >
                            <i className="ri-search-line align-bottom me-1"></i> Search
                        </button>

                        <button
                            className="btn btn-danger"
                            onClick={handleReset}
                        >
                            <i className="ri-refresh-line align-bottom me-1"></i> Reset
                        </button>
                    </div>
                  </div>

                  {/* --- Table Section --- */}
                  <div className="table-responsive table-card p-2">
                    <table
                      className="table align-middle table-striped table-hover"
                      id="reportTable"
                    >
                      <thead className="bg-soft-info bg-gradient">
  <tr>
    <th>Id</th>
    <th>Booking No</th>
    <th>PNR</th> {/* <--- ADD THIS */}
    <th>Traveler Name</th>
    <th>Date</th>
    <th>Booking Status</th>
    <th>Payment Status</th>
    <th>Purchase Price</th>
    <th>Sale Price</th>
    <th>Profit</th>
  </tr>
</thead>
                      {pageLoading ? (
                        <tbody>
                          <tr>
                            <td colSpan="9" className="text-center">
                              <img
                                src="/assets/images/loader.webp"
                                width="64px"
                                height="64px"
                                alt="Loading..."
                              />
                              <p>Loading Report, Please wait...</p>
                            </td>
                          </tr>
                        </tbody>
                      ) : (
                        <>
                          <tbody className="list form-check-all">
                            {lists.length > 0 ? (
                              lists.map((booking, index) => (
                                <tr key={booking.flightBookingId || index}>
                                  <td>{index + 1}</td>
                                  <td>{booking.bookingNo}</td>
                                   <td>
            <span className="fw-bold text-primary">
                {booking.pnr || "-"}
            </span>
        </td>
                                  <td>{booking.travellerName}</td>
                                  {/* Added Date Column for better reporting */}
                                  <td>{formatDate(booking.bookingDate)}</td>
                                  <td>
                                    {booking.bookingStatus === "CONFIRMED" ? (
                                      <span className="badge bg-success">
                                        {booking.bookingStatus}
                                      </span>
                                    ) : booking.bookingStatus === "CANCELLED" ? (
                                        <span className="badge bg-danger">
                                        {booking.bookingStatus}
                                      </span>
                                    ) : (
                                      <span className="badge bg-warning text-dark">
                                        {booking.bookingStatus}
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {booking.paymentStatus === "SUCCESS" ? (
                                      <span className="badge bg-success">
                                        {booking.paymentStatus}
                                      </span>
                                    ) : (
                                      <span className="badge bg-danger">
                                        {booking.paymentStatus}
                                      </span>
                                    )}
                                  </td>
                                  <td>{booking.totalFare?.toFixed(2)}</td>
                                  <td>{booking.totalPrice?.toFixed(2)}</td>
                                  <td className="fw-bold text-success">
                                    {booking.convenienceFee?.toFixed(2)}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" className="text-center">
                                  No records found matching filters.
                                </td>
                              </tr>
                            )}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td
                                colSpan="8"
                                className="text-end fw-bold fs-14"
                              >
                                Grand Total Profit:
                              </td>
                              <td className="fw-bold fs-14 text-success">
                                {totalProfit.toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        </>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightReport;